import { Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { IconComponent, PopupComponent } from 'continity-shared-ui';
import { ShellProduct } from './shell-product';

/**
 * Cross-portal product switcher (922 Entscheidung 8, Option B — ADO #1543 / #1522).
 *
 * Compact popup-button form: a single `app-popup` (tooltip variant) trigger — the
 * "layers" icon — that opens a dropdown of products. The `current` product is a
 * highlighted, non-clickable row; siblings are full-page `<a [href]>` links (they target
 * a DIFFERENT SPA, so NOT routerLink). Labels are config-driven brand names → NOT i18n'd.
 *
 * This mirrors the MAIN portal's switcher 1:1 (same `app-popup variant=tooltip
 * position=bottom offset=10`, same `data-cy="product-switcher-trigger"`) so that — once
 * `cs-shell` relocates this node into the app-shell logo row at runtime — it sits in the
 * exact same place AND form as the main portal's logo-corner switcher.
 *
 * NB: the previous inline-list form lived in the sidebar-nav; the compact button is what
 * fits the logo corner. The per-product `data-cy="cs-shell-product-<key>"` hooks are kept.
 */
@Component({
  selector: 'cs-product-switcher',
  standalone: true,
  imports: [CommonModule, IconComponent, PopupComponent],
  template: `
    @if (products.length) {
      <app-popup class="relative z-[60]" variant="tooltip" position="bottom" [offset]="10">
        <span popup-trigger class="cs-switch-trigger"
          [attr.title]="triggerLabel || null"
          [attr.aria-label]="triggerLabel || null"
          data-cy="product-switcher-trigger">
          <app-icon name="layers" [size]="20"></app-icon>
        </span>
        <div popup-content class="cs-switch-panel">
          @for (product of products; track product.key) {
            @if (product.current) {
              <div class="cs-switch-item cs-switch-item-current"
                [attr.data-cy]="'cs-shell-product-' + product.key">
                @if (product.icon) {
                  <app-icon [name]="product.icon" [size]="16"></app-icon>
                }
                <span>{{ product.label }}</span>
                <app-icon name="check" [size]="14" class="cs-switch-check"></app-icon>
              </div>
            } @else {
              <a class="cs-switch-item" [href]="product.url"
                [attr.data-cy]="'cs-shell-product-' + product.key">
                @if (product.icon) {
                  <app-icon [name]="product.icon" [size]="16"></app-icon>
                }
                <span>{{ product.label }}</span>
              </a>
            }
          }
        </div>
      </app-popup>
    }
  `,
  styles: [
    `
      /* Compact trigger button — mirrors the main portal's logo-corner switcher
         (h-9 w-9, rounded, subtle hover). */
      .cs-switch-trigger {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        height: 2.25rem;
        width: 2.25rem;
        border-radius: 0.375rem;
        color: inherit;
        cursor: pointer;
      }
      .cs-switch-trigger:hover {
        background: rgba(127, 127, 127, 0.12);
      }
      /* Dropdown panel (app-popup already supplies bg-card / shadow / padding). */
      .cs-switch-panel {
        min-width: 11rem;
        display: flex;
        flex-direction: column;
        gap: 0.125rem;
      }
      .cs-switch-item {
        display: flex;
        align-items: center;
        gap: 0.5rem;
        padding: 0.5rem;
        border-radius: 0.375rem;
        font-size: 0.875rem;
        color: inherit;
        text-decoration: none;
      }
      a.cs-switch-item:hover {
        background: rgba(127, 127, 127, 0.12);
      }
      .cs-switch-item-current {
        color: #f97316;
        font-weight: 600;
        cursor: default;
      }
      .cs-switch-check {
        margin-left: auto;
      }
    `,
  ],
})
export class ProductSwitcherComponent {
  /**
   * Cross-portal product entries. Empty → the switcher renders nothing.
   * Siblings render as links; the `current` product is highlighted + non-clickable.
   */
  @Input() products: ShellProduct[] = [];
  /** Already-translated tooltip/aria-label for the trigger button (e.g. "Produkt wechseln"). */
  @Input() triggerLabel = '';
}
